import { useEffect, useState } from "react";
import {
    BookOpen,
    Brain,
    ChartLineUp,
    ChatTeardrop,
    Crown,
    FilmSlate,
    Gauge,
    GearSix,
    Lightning,
    Microphone,
    PaperPlaneTilt,
    PlayCircle,
    Rocket,
    Trophy,
} from "@phosphor-icons/react";

/**
 * DashboardNav — sticky in-page section pills so members can jump quickly
 * around their dashboard without endless scrolling.
 *
 * Each pill targets an `id` on a major dashboard section. Active pill is
 * highlighted via IntersectionObserver as the user scrolls.
 */
const SECTIONS = [
    { id: "alpha-feed", label: "ALPHA FEED", icon: Lightning },
    { id: "billing", label: "BILLING", icon: Gauge },
    { id: "script-engine", label: "SCRIPT ENGINE", icon: Microphone },
    { id: "leaderboard", label: "LEADERBOARD", icon: Trophy },
    { id: "recipe-vault", label: "RECIPE VAULT", icon: BookOpen },
    { id: "growth-playbook", label: "PLAYBOOKS", icon: ChartLineUp },
    { id: "deployment", label: "DEPLOY HUB", icon: Rocket },
    { id: "niche-generator", label: "NICHE AI", icon: Brain },
    { id: "sample-gallery", label: "SAMPLES", icon: FilmSlate },
    { id: "walkthrough", label: "WALKTHROUGH", icon: PlayCircle },
    { id: "prompt-lab", label: "PROMPT LAB", icon: PaperPlaneTilt },
    { id: "feedback", label: "FEEDBACK", icon: ChatTeardrop },
    { id: "account", label: "ACCOUNT", icon: GearSix },
];

export default function DashboardNav({ tierBadge }) {
    const [active, setActive] = useState(SECTIONS[0].id);

    useEffect(() => {
        const els = SECTIONS
            .map((s) => document.getElementById(s.id))
            .filter(Boolean);
        if (els.length === 0) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActive(e.target.id);
                });
            },
            { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
        );
        els.forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, []);

    const goto = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <nav
            data-testid="dashboard-nav"
            className="sticky top-[68px] z-40 bg-background/95 backdrop-blur-md border-y border-electric-blue/15 -mx-4 md:-mx-8 lg:-mx-12 px-4 md:px-8 lg:px-12 mb-6"
        >
            <div className="flex items-center gap-3 overflow-x-auto py-3 scrollbar-thin">
                {tierBadge && (
                    <div className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 border border-electric-blue/40 bg-electric-blue/10">
                        <Crown size={12} weight="fill" className="text-electric-blue" />
                        <span className="label-mono text-[10px] text-electric-blue tracking-wider">
                            {tierBadge}
                        </span>
                    </div>
                )}
                <div className="flex items-center gap-1.5">
                    {SECTIONS.map((s) => {
                        const Icon = s.icon;
                        const isActive = active === s.id;
                        return (
                            <button
                                key={s.id}
                                onClick={() => goto(s.id)}
                                data-testid={`dashboard-nav-${s.id}`}
                                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 border transition-all label-mono text-[10px] tracking-wider ${
                                    isActive
                                        ? "border-electric-blue text-electric-blue bg-electric-blue/10 shadow-[0_0_12px_rgba(0,123,255,0.3)]"
                                        : "border-white/10 text-muted-foreground hover:border-electric-blue/50 hover:text-white"
                                }`}
                            >
                                <Icon size={11} weight={isActive ? "fill" : "bold"} />
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        </nav>
    );
}
